import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import teacherService from '../../services/api/teacher';
import { useQuery } from 'react-query';
import { formatDate } from '../../services/utils/date';
import {
  Users,
  Settings,
  User,
  GraduationCap,
  BarChart,
  CreditCard,
  TrendingUp,
  Search,
  CheckCircle2,
  Clock,
  ShieldCheck,
  LayoutDashboard,
  Sparkles,
  ChevronLeft,
  HelpCircle,
  BookOpen,
  Calendar,
  ArrowLeft
} from 'lucide-react';

import TeacherStatus from '../../components/teacher/TeacherStatus';
import PendingApprovalMessage from '../../components/teacher/PendingApprovalMessage';

const Dashboard = () => {
  const { user, isAdmin, isTeacher, isStudent } = useAuth();
  const navigate = useNavigate();
  
  // جلب بيانات المدرس (مفعل فقط إذا كان المستخدم مدرساً)
  const { data: teacherInfo } = useQuery('teacher-info', async () => {
    const response = await teacherService.getTeacherInfo();
    // The response structure might be different - return the response directly if data is undefined
    return response.data || response;
  }, {
    retry: 1,
    enabled: isTeacher,
  });

  const getWelcomeMessage = () => {
    if (isAdmin) return 'مرحباً بك في النظرة العامة على الإدارة';
    if (isTeacher) return 'مرحباً بك في فضائك التعليمي';
    if (isStudent) return 'مرحباً بك في رحلتك التعليمية';
    return 'مرحباً بك في المنصة';
  };

  const getDashboardActions = () => {
    if (isAdmin) {
      return [
        {
          title: 'إدارة المستخدمين',
          description: 'عرض وإدارة صلاحيات جميع المستخدمين',
          icon: Users,
          color: 'bg-blue-600',
          shadow: 'shadow-blue-100',
          action: () => navigate('/admin/users')
        },
        {
          title: 'طلبات المدرسين',
          description: 'مراجعة بيانات المدرسين الجدد واعتمادهم',
          icon: GraduationCap,
          color: 'bg-teal-600',
          shadow: 'shadow-teal-100',
          action: () => navigate('/admin/teachers')
        },
        {
          title: 'الرقابة على الكورسات',
          description: 'متابعة كافة المحتويات المرفوعة على المنصة',
          icon: BookOpen,
          color: 'bg-purple-600',
          shadow: 'shadow-purple-100',
          action: () => navigate('/admin/courses')
        },
        {
          title: 'إعدادات النظام',
          description: 'تخصيص الخيارات العامة والتقنية للمنصة',
          icon: Settings,
          color: 'bg-rose-600',
          shadow: 'shadow-rose-100',
          action: () => navigate('/admin/settings')
        }
      ];
    }

    if (isTeacher) {
      if (teacherInfo?.status === 'approved') {
        return [
          {
            title: 'إدارة الكورسات',
            description: 'رفع دروس جديدة وتعديل المحتوى الحالي',
            icon: BookOpen,
            color: 'bg-blue-600',
            shadow: 'shadow-blue-100',
            action: () => navigate('/teacher/courses')
          },
          {
            title: 'قائمة الطلاب',
            description: 'متابعة الطلاب المشتركين في موادك',
            icon: Users,
            color: 'bg-emerald-600',
            shadow: 'shadow-emerald-100',
            action: () => navigate('/teacher/students')
          },
          {
            title: 'غرفة الأسئلة',
            description: 'التفاعل مع استفسارات الطلاب والرد عليها',
            icon: HelpCircle,
            color: 'bg-violet-600',
            shadow: 'shadow-violet-100',
            action: () => navigate('/teacher/questions')
          },
          {
            title: 'تقارير الأداء',
            description: 'عرض إحصائيات المبيعات ونسبة المشاهدة',
            icon: BarChart,
            color: 'bg-orange-600',
            shadow: 'shadow-orange-100',
            action: () => navigate('/teacher/analytics')
          }
        ];
      } else {
        return [
          {
            title: 'تعديل الملف الشخصي',
            description: 'تحديث بياناتك المهنية والسيرة الذاتية',
            icon: User,
            color: 'bg-blue-600',
            shadow: 'shadow-blue-100',
            action: () => navigate('/teacher/profile')
          },
          {
            title: 'تخصصاتي الدراسية',
            description: 'إدارة المواد والصفوف التي تقوم بتدريسها',
            icon: GraduationCap,
            color: 'bg-amber-600',
            shadow: 'shadow-amber-100',
            action: () => navigate('/teacher/subjects')
          }
        ];
      }
    }

    if (isStudent) {
      return [
        {
          title: 'دروسي الحالية',
          description: 'متابعة الدروس التي بدأت في دراستها',
          icon: BookOpen,
          color: 'bg-blue-600',
          shadow: 'shadow-blue-100',
          action: () => navigate('/student/lessons')
        },
        {
          title: 'استكشاف الكورسات',
          description: 'البحث عن مواد جديدة ومدرسين متميزين',
          icon: Search,
          color: 'bg-indigo-600',
          shadow: 'shadow-indigo-100',
          action: () => navigate('/student/browse')
        },
        {
          title: 'سجل الاشتراكات',
          description: 'متابعة حالة الدفع ومدة صلاحية اشتراكك',
          icon: CreditCard,
          color: 'bg-emerald-600',
          shadow: 'shadow-emerald-100',
          action: () => navigate('/student/subscriptions')
        },
        {
          title: 'أسئلتي',
          description: 'متابعة الردود على استفساراتك التعليمية',
          icon: HelpCircle,
          color: 'bg-orange-600',
          shadow: 'shadow-orange-100',
          action: () => navigate('/student/questions')
        }
      ];
    }

    return [];
  };

  const actions = getDashboardActions();

  return (
    <div className="max-w-7xl mx-auto space-y-8 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Welcome Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 rounded-[2.5rem] p-8 lg:p-12 text-white shadow-2xl shadow-primary-200">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/10 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-right">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-4">
              <Sparkles size={14} /> {user?.role === 'admin' ? 'النظرة العامة' : 'منصة التعلم الذكي'}
            </div>
            <h1 className="text-3xl lg:text-5xl font-black mb-3 leading-tight">{getWelcomeMessage()}</h1>
            <p className="text-primary-100 text-lg font-medium opacity-90 max-w-xl">
              أهلاً بك يا <span className="text-white font-black underline decoration-white/30 underline-offset-4">{user?.name}</span>، يسعدنا رؤيتك اليوم في المنصة.
            </p>
          </div>

          <div className="flex flex-col items-center gap-4">
            {isTeacher && teacherInfo && (
              <div className="transform scale-110">
                <TeacherStatus status={teacherInfo.status} />
              </div>
            )}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 px-6 py-3 rounded-2xl text-center">
              <p className="text-[10px] font-black uppercase tracking-widest opacity-70">اليوم هو</p>
              <p className="font-bold text-lg">{formatDate(new Date())}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Teacher Pending Alert */}
      {isTeacher && teacherInfo?.status === 'pending' && (
        <div className="animate-in fade-in slide-in-from-top duration-700">
            <PendingApprovalMessage />
        </div>
      )}

      {/* Email Verification Status Alert */}
      {user && (
        <div className="animate-in fade-in slide-in-from-top duration-700">
          {user.emailVerified ? (
            <div className="bg-emerald-50 border border-emerald-100 rounded-[1.8rem] px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <p className="text-xs font-black text-emerald-700 uppercase tracking-widest">البريد الإلكتروني موثّق</p>
                  <p className="text-sm text-emerald-800 font-medium">
                    تم تأكيد بريدك الإلكتروني بنجاح، يمكنك الاستفادة من جميع مزايا المنصة بأمان.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-100 rounded-[1.8rem] px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="text-xs font-black text-amber-700 uppercase tracking-widest">بريد يحتاج إلى تأكيد</p>
                  <p className="text-sm text-amber-800 font-medium">
                    لم يتم توثيق بريدك الإلكتروني بعد. يمكنك طلب رابط التفعيل من صفحة <span className="font-black underline underline-offset-2">ملفي الشخصي</span>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Account Info Bar (Quick Stats) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition-all group">
          <div className="w-14 h-14 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition-all duration-300">
            <ShieldCheck size={28} />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">حالة الحساب</p>
            <p className="text-xl font-black text-gray-900">نشط ومؤمن</p>
          </div>
        </div>

        <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition-all group">
          <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
            <User size={28} />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">المستخدم</p>
            <p className="text-xl font-black text-gray-900 capitalize">
                {user?.role === 'admin' && 'المدير العام'}
                {user?.role === 'teacher' && 'مدرس معتمد'}
                {user?.role === 'student' && 'طالب'}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-[2rem] p-6 shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition-all group">
          <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-all duration-300">
            <Calendar size={28} />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">عضو منذ تاريخ</p>
            <p className="text-xl font-black text-gray-900">
                {user?.created_at ? formatDate(user.created_at) : (user?.createdAt ? formatDate(user.createdAt) : 'حديثاً')}
            </p>
          </div>
        </div>
      </div>

      {/* Actions Grid Area */}
      <div className="space-y-6">
        <div className="flex items-center gap-2 px-2">
            <LayoutDashboard size={22} className="text-primary-600" />
            <h2 className="text-2xl font-black text-gray-900">{isAdmin ? 'المهام الإدارية' : 'الوصول السريع'}</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {actions.map((action, index) => {
            const Icon = action.icon;
            return (
                <button
                    key={index}
                    onClick={action.action}
                    className="bg-white rounded-[2.2rem] shadow-sm hover:shadow-2xl transition-all duration-300 p-8 text-right group border border-gray-100 hover:border-primary-500 relative overflow-hidden"
                >
                    <div className="absolute -top-10 -left-10 w-32 h-32 bg-gray-50 rounded-full group-hover:bg-primary-50 transition-colors"></div>
                    
                    <div className={`${action.color} relative z-10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-xl ${action.shadow} group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                        <Icon size={32} className="text-white" />
                    </div>
                    
                    <div className="relative z-10">
                        <h3 className="text-xl font-black text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                            {action.title}
                        </h3>
                        <p className="text-sm text-gray-500 font-medium leading-relaxed mb-6 h-10 line-clamp-2">
                            {action.description}
                        </p>
                        <div className="flex items-center text-primary-600 text-sm font-black uppercase tracking-tighter">
                            تصفح القسم الآن <ChevronLeft size={18} className="mr-1 group-hover:translate-x-[-4px] transition-transform" />
                        </div>
                    </div>
                </button>
            );
            })}
        </div>
      </div>

      {/* Activity Section Table-style */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                <h2 className="text-xl font-black text-gray-900 flex items-center gap-3">
                    <TrendingUp className="text-primary-600" />
                    سجل النشاط الأخير
                </h2>
                <button className="text-xs font-bold text-gray-400 hover:text-primary-600 transition-colors">تحديث القائمة</button>
            </div>
            <div className="divide-y divide-gray-50">
                <div className="flex items-center justify-between p-8 hover:bg-gray-50/50 transition-colors group">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <BookOpen size={24} />
                        </div>
                        <div>
                            <p className="text-base font-bold text-gray-900">مرحباً بك في المنصة</p>
                            <p className="text-sm text-gray-500">لقد تم تسجيل دخولك بنجاح إلى لوحة التحكم الخاصة بك.</p>
                        </div>
                    </div>
                    <div className="text-left">
                        <span className="text-[10px] font-black text-primary-600 bg-primary-50 px-3 py-1 rounded-full uppercase">الآن</span>
                    </div>
                </div>
                
                <div className="flex items-center justify-between p-8 hover:bg-gray-50/50 transition-colors group">
                    <div className="flex items-center gap-4 opacity-60">
                        <div className="w-12 h-12 bg-gray-100 text-gray-500 rounded-2xl flex items-center justify-center">
                            <Clock size={24} />
                        </div>
                        <div>
                            <p className="text-base font-bold text-gray-900">بدء الجلسة</p>
                            <p className="text-sm text-gray-500">تم تهيئة كافة أدوات المساعدة والبحث الخاصة بك.</p>
                        </div>
                    </div>
                    <div className="text-left">
                        <span className="text-[10px] font-black text-gray-400 bg-gray-100 px-3 py-1 rounded-full uppercase">منذ لحظات</span>
                    </div>
                </div>
            </div>
        </div>

        {/* Motivational Side Card */}
        <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden shadow-2xl group">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl"></div>
            <div className="relative z-10 flex flex-col h-full">
                <div className="bg-primary-600 w-14 h-14 rounded-2xl flex items-center justify-center mb-8 shadow-xl">
                    <Sparkles size={28} />
                </div>
                <h3 className="text-2xl font-black mb-4 leading-tight">اجعل اليوم خطوة نحو النجاح!</h3>
                <p className="text-gray-400 font-medium mb-10 leading-relaxed">
                    تذكر أن الاستمرارية هي مفتاح التفوق. تابع دروسك اليومية وشارك في المناقشات التعليمية لتصل إلى أهدافك.
                </p>
                <div className="mt-auto">
                    <button 
                        onClick={() => navigate(isStudent ? '/student/browse' : '/teacher/courses')}
                        className="w-full bg-white text-gray-900 font-black py-4 rounded-2xl hover:bg-primary-50 transition-all flex items-center justify-center gap-2 transform active:scale-95 shadow-xl"
                    >
                        ابدأ العمل الآن <ArrowLeft size={20} />
                    </button>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;